/**
 * Unified date formatting utilities.
 *
 * Replaces ~10 duplicate formatDate functions and ~25 inline
 * toLocaleDateString() calls scattered across the codebase.
 *
 * All functions accept an ISO-8601 string, Date, or undefined/null
 * and return a formatted string (or a fallback for missing values).
 */

const LOCALE = 'en-US' as const;

// ---------------------------------------------------------------------------
// Primary formatters
// ---------------------------------------------------------------------------

/**
 * "Feb 9, 2026" — short month, numeric day + year.
 * Most common pattern; use for tables, cards, lists.
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * "February 9, 2026" — long month, numeric day + year.
 * Use for detail pages, hero sections, formal contexts.
 */
export function formatDateLong(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * "February 9, 2026 at 02:30 PM" — long date + time.
 * Use for audit trails, timestamps, submission confirmations.
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * "Feb 9" — short month + day, no year.
 * Use for timelines, compact date ranges.
 */
export function formatDateCompact(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * "Feb 2026" — short month + year, no day.
 * Use for monthly views, report dates.
 */
export function formatMonthYear(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'short',
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDate(value: string | Date): Date | null {
  try {
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}
